import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { API_URLS } from '../constants/apiUrl';
import { useLoading } from '../context/LoadingContext';
import { patch } from '../api/api';
import { showToast } from '../components/utils/toast';
import { IUpdateWorkTimeDto, IWorkTime } from '../interface/workTimes';
import { useAnalytics } from './useAnalytics';
import { AnalyticsFeatureOperation } from '../interface/analytics';
import { isVersionConflictError, VERSION_CONFLICT_CONFIG } from '../helpers/versionConflictHelper';

const updateWorktime = async (params: IUpdateWorkTimeDto): Promise<IWorkTime> => {
  const { id, ...data } = params;
  return patch(`${API_URLS.WORK_TIMES}/${id}`, data, VERSION_CONFLICT_CONFIG);
};

export const useUpdateWorktime = (): UseMutationResult<
  IWorkTime,
  AxiosError<{ message?: string; type?: string }>,
  IUpdateWorkTimeDto
> => {
  const { setIsLoading } = useLoading();
  const { t } = useTranslation();
  const { trackBusinessEvent } = useAnalytics();

  return useMutation({
    mutationFn: updateWorktime,
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      trackBusinessEvent(AnalyticsFeatureOperation.WORKTIME_UPDATED);
      showToast('success', t('worktime:workTimeEdited'));
    },
    onError: (error) => {
      if (isVersionConflictError(error)) return;
      showToast('error', error.message);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });
};
